import { FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleTime } from '@visx/scale';
import { Avatar, Button, Dropdown, Menu, Space } from 'antd';
import { capitalCase } from 'change-case';
import { motion, useReducedMotion } from 'framer-motion';
import { TbActivity, TbChevronDown, TbExclamationCircle, TbLock, TbMinus, TbPlus, TbTimeline } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import WorkspaceResourceAvatar from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';

import {
    ACTIVITY_TIMELINE_CADENCES,
    ActivityTimelineCadenceId,
    ActivityTimelineTickUnit,
    aggregateActivityTimelineEvents,
    getActivityTimelineCadence,
    getActivityTimelineParticipants,
    getActivityTimelineTickHierarchy,
    getActivityTimelineWindow,
    stepActivityTimelineCadence,
    WorkspaceActivityTimelineCluster,
    WorkspaceActivityTimelineEvent,
} from './model';

interface WorkspaceActivityTimelineProps {
    availableCadences: ActivityTimelineCadenceId[];
    cadence: ActivityTimelineCadenceId;
    error?: boolean;
    events: WorkspaceActivityTimelineEvent[];
    loading?: boolean;
    onCadenceChange: (cadence: ActivityTimelineCadenceId) => void;
    onRetry?: () => void;
    startDate?: Date | string;
}

interface PositionedCluster extends WorkspaceActivityTimelineCluster {
    x: number;
}

const MIN_TIMELINE_ZOOM = 1;
const MAX_TIMELINE_ZOOM = 8;
const ACTIVITY_CLUSTER_WIDTH = '8.5rem';
const ACTIVITY_CLUSTER_MINIMUM_SPACING = 144;
const ACTIVITY_CLUSTER_EDGE_GUTTER = 72;

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), maximum);

interface TimelineTickDefinition {
    step: number;
    unit: ActivityTimelineTickUnit;
}

const floorTimelineDate = (value: Date, definition: TimelineTickDefinition): Date => {
    const date = new Date(value);
    const { step, unit } = definition;

    date.setMilliseconds(0);
    if (unit === 'minute') {
        date.setSeconds(0);
        date.setMinutes(Math.floor(date.getMinutes() / step) * step);
    } else if (unit === 'hour') {
        date.setSeconds(0);
        date.setMinutes(0);
        date.setHours(Math.floor(date.getHours() / step) * step);
    } else if (unit === 'day') {
        date.setHours(0, 0, 0, 0);
    } else if (unit === 'week') {
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    } else if (unit === 'month') {
        date.setHours(0, 0, 0, 0);
        date.setDate(1);
        date.setMonth(Math.floor(date.getMonth() / step) * step);
    } else if (unit === 'quarter') {
        date.setHours(0, 0, 0, 0);
        date.setDate(1);
        date.setMonth(Math.floor(date.getMonth() / 3) * 3);
    } else {
        date.setHours(0, 0, 0, 0);
        date.setDate(1);
        date.setMonth(0);
        date.setFullYear(Math.floor(date.getFullYear() / step) * step);
    }

    return date;
};

const addTimelineTick = (value: Date, definition: TimelineTickDefinition): Date => {
    const next = new Date(value);
    const { step, unit } = definition;

    if (unit === 'minute') next.setMinutes(next.getMinutes() + step);
    else if (unit === 'hour') next.setHours(next.getHours() + step);
    else if (unit === 'day') next.setDate(next.getDate() + step);
    else if (unit === 'week') next.setDate(next.getDate() + 7 * step);
    else if (unit === 'month') next.setMonth(next.getMonth() + step);
    else if (unit === 'quarter') next.setMonth(next.getMonth() + 3 * step);
    else next.setFullYear(next.getFullYear() + step);

    return next;
};

const generateTimelineTicks = (start: Date, end: Date, definition: TimelineTickDefinition): Date[] => {
    const ticks: Date[] = [];
    let cursor = floorTimelineDate(start, definition);

    if (cursor < start) cursor = addTimelineTick(cursor, definition);

    for (let index = 0; cursor <= end && index < 5000; index += 1) {
        ticks.push(cursor);
        cursor = addTimelineTick(cursor, definition);
    }

    return ticks;
};

const formatMajorTimelineTick = (tick: Date, cadence: ActivityTimelineCadenceId): string => {
    if (cadence === 'day' || cadence === 'multi-day' || cadence === 'week') {
        return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' }).format(tick);
    }
    if (cadence === 'multi-week' || cadence === 'month') {
        return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' }).format(tick);
    }
    if (cadence === 'quarter' || cadence === 'multi-month') {
        return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(tick).toUpperCase();
    }
    if (cadence === 'multi-quarter' || cadence === 'year') {
        return `Q${Math.floor(tick.getMonth() / 3) + 1} ${tick.getFullYear()}`;
    }
    return String(tick.getFullYear());
};

const formatTimelineDate = (value: Date | string): string =>
    new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(new Date(value));

const mergeNearbyClusters = (
    clusters: WorkspaceActivityTimelineCluster[],
    xForDate: (date: Date) => number,
    minimumSpacing = ACTIVITY_CLUSTER_MINIMUM_SPACING,
): PositionedCluster[] =>
    clusters.reduce<PositionedCluster[]>((positioned, cluster) => {
        const x = xForDate(cluster.timestamp);
        const previous = positioned[positioned.length - 1];

        if (previous && x - previous.x < minimumSpacing) {
            const events = [...previous.events, ...cluster.events].sort(
                (left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
            );
            const timestamp = new Date(
                events.reduce((total, event) => total + new Date(event.timestamp).getTime(), 0) / events.length,
            );

            positioned[positioned.length - 1] = {
                ...previous,
                bucketEnd: cluster.bucketEnd,
                events,
                id: `${previous.id}-${cluster.id}`,
                timestamp,
                x: xForDate(timestamp),
            };
        } else {
            positioned.push({ ...cluster, x });
        }

        return positioned;
    }, []);

const ActivityActorAvatar: FC<{ actor: { avatar?: string; id: string; name: string } }> = ({ actor }) => {
    const { token } = useAntDesignTheme();
    const initials = actor.name
        ?.split(/\s+/)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <Avatar
            alt={actor.name}
            size={28}
            src={actor.avatar}
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBgContainer}`,
                color: token.colorTextSecondary,
                fontSize: token.fontSizeSM,
            }}
        >
            {initials || <TbActivity size='0.875rem' />}
        </Avatar>
    );
};

const ActivityClusterAvatarGroups: FC<{ events: WorkspaceActivityTimelineEvent[] }> = ({ events }) => {
    const { token } = useAntDesignTheme();
    const { actors, resources } = useMemo(() => getActivityTimelineParticipants(events), [events]);
    const overflowAvatarStyle = {
        backgroundColor: token.colorBgBase,
        border: `1px solid ${token.colorBorder}`,
        color: token.colorTextTertiary,
        fontSize: 9,
    };

    return (
        <span
            aria-label={`${actors.length} unique ${actors.length === 1 ? 'user' : 'users'} affected ${resources.length} unique ${resources.length === 1 ? 'resource' : 'resources'}`}
            style={{
                alignItems: 'flex-start',
                display: 'inline-flex',
                justifyContent: 'center',
                lineHeight: 0,
                paddingBottom: '0.375rem',
                paddingRight: '0.625rem',
                position: 'relative',
            }}
        >
            <Avatar.Group max={{ count: 3, style: overflowAvatarStyle }} size={28}>
                {actors.map((actor) => (
                    <ActivityActorAvatar actor={actor} key={actor.id || actor.name} />
                ))}
            </Avatar.Group>
            <span style={{ bottom: 0, position: 'absolute', right: 0 }}>
                <Avatar.Group max={{ count: 3, style: { ...overflowAvatarStyle, fontSize: 8 } }} size={18}>
                    {resources.map((resource) => (
                        <WorkspaceResourceAvatar
                            aria-label={resource.name}
                            key={`${resource.type}:${resource.id}`}
                            resource={resource.type}
                            size={18}
                        />
                    ))}
                </Avatar.Group>
            </span>
        </span>
    );
};

const ActivityClusterDropdown: FC<{
    children: ReactNode;
    cluster: WorkspaceActivityTimelineCluster;
}> = ({ children, cluster }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const filteredEvents = cluster.events.filter((event) =>
        `${event.actor?.name ?? ''} ${event.action} ${event.resourceName} ${event.resourceType}`
            .toLowerCase()
            .includes(search.toLowerCase()),
    );
    const panel = (
        <DropdownPanel
            footer={<LearnMoreFooter topic='workspace activity and usage analytics' variant='dropdown' />}
            maxHeight='20rem'
            search={{
                ariaLabel: 'Search activity events',
                placeholder: 'Search Activity Events...',
                setValue: setSearch,
                value: search,
            }}
            width='23rem'
        >
            <DropdownMenuLabel icon={<TbTimeline size='0.875rem' />}>
                {cluster.events.length} {cluster.events.length === 1 ? 'Event' : 'Events'} ·{' '}
                {formatTimelineDate(cluster.timestamp)}
            </DropdownMenuLabel>
            {filteredEvents.length ? (
                <DropdownPanelMenu selectable={false}>
                    {filteredEvents.map((event) => (
                        <Menu.Item key={event.id}>
                            <DropdownOptionContent
                                avatar={{
                                    icon: event.actor?.avatar ? undefined : <TbActivity size='0.875rem' />,
                                    props: { shape: 'circle', size: 20 },
                                    src: event.actor?.avatar,
                                }}
                                metadata={[capitalCase(event.resourceType), formatTimelineDate(event.timestamp)]}
                                singleLine
                                title={
                                    <>
                                        {event.actor?.name ?? 'Workspace activity'} {event.action}{' '}
                                        <strong>{event.resourceName}</strong>
                                    </>
                                }
                            />
                        </Menu.Item>
                    ))}
                </DropdownPanelMenu>
            ) : (
                <EmptyView
                    description='No events in this group match your search.'
                    height='9rem'
                    icon={<TbTimeline size='1.25rem' />}
                    label='No Matching Events'
                />
            )}
        </DropdownPanel>
    );

    return (
        <Dropdown
            destroyOnHidden
            open={open}
            popupRender={() => panel}
            placement='topLeft'
            trigger={['hover', 'click']}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setSearch('');
                }
            }}
        >
            {children}
        </Dropdown>
    );
};

const TimelineSkeleton: FC = () => {
    const { token } = useAntDesignTheme();
    const clusters = [
        { avatarCount: 2, left: '18%', labelWidth: '5.5rem', titleWidth: '7.25rem' },
        { avatarCount: 3, left: '48%', labelWidth: '4.75rem', titleWidth: '8.5rem' },
        { avatarCount: 2, left: '77%', labelWidth: '5.25rem', titleWidth: '6.75rem' },
    ];

    return (
        <div
            aria-label='Loading workspace activity timeline'
            role='status'
            style={{ height: '15rem', overflow: 'hidden', position: 'relative' }}
        >
            {[12, 26, 40, 54, 68, 82, 96].map((left) => (
                <span
                    aria-hidden='true'
                    key={left}
                    style={{ left: `${left}%`, position: 'absolute', top: '2rem', transform: 'translateX(-50%)' }}
                >
                    <ButtonSkeleton size='small' style={{ height: '0.625rem', width: '2.75rem' }} />
                    <span
                        style={{
                            background: token.colorTextQuaternary,
                            display: 'block',
                            height: '1.25rem',
                            margin: '0.75rem auto 0',
                            width: 2,
                        }}
                    />
                </span>
            ))}
            <div
                aria-hidden='true'
                style={{
                    background: `linear-gradient(90deg, transparent, ${token.colorBorderSecondary} 4%, ${token.colorBorderSecondary} 96%, transparent)`,
                    height: 2,
                    left: 0,
                    position: 'absolute',
                    right: 0,
                    top: 76,
                }}
            />
            {clusters.map((cluster) => (
                <div
                    key={cluster.left}
                    style={{
                        left: cluster.left,
                        position: 'absolute',
                        top: '6.25rem',
                        transform: 'translateX(-50%)',
                    }}
                >
                    <span style={{ display: 'flex', paddingLeft: '0.25rem' }}>
                        {Array.from({ length: cluster.avatarCount }, (_, index) => (
                            <span key={index} style={{ marginLeft: index ? '-0.45rem' : 0 }}>
                                <AvatarSkeleton size={32} />
                            </span>
                        ))}
                    </span>
                    <ButtonSkeleton
                        size='small'
                        style={{
                            display: 'block',
                            height: '0.625rem',
                            marginTop: '0.25rem',
                            width: cluster.labelWidth,
                        }}
                    />
                    <ButtonSkeleton
                        size='small'
                        style={{
                            display: 'block',
                            height: '0.875rem',
                            marginTop: '0.375rem',
                            width: cluster.titleWidth,
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

interface TimelineViewportProps {
    cadence: ActivityTimelineCadenceId;
    clusters: WorkspaceActivityTimelineCluster[];
    gradientId: string;
    viewportWidth: number;
    window: { end: Date; start: Date };
}

const TimelineViewport: FC<TimelineViewportProps> = ({ cadence, clusters, gradientId, viewportWidth, window }) => {
    const { token } = useAntDesignTheme();
    const reduceMotion = useReducedMotion();
    const viewportRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef(MIN_TIMELINE_ZOOM);
    const [zoom, setZoom] = useState(MIN_TIMELINE_ZOOM);
    const [scrollState, setScrollState] = useState({ left: false, right: false });
    const trackWidth = Math.max(viewportWidth, viewportWidth * zoom);
    const span = window.end.getTime() - window.start.getTime();
    const displayEnd = useMemo(() => new Date(window.end.getTime() + span * 0.2), [span, window.end]);

    const updateScrollState = useCallback(() => {
        const viewport = viewportRef.current;

        if (!viewport) return;

        setScrollState({
            left: viewport.scrollLeft > 2,
            right: viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 2,
        });
    }, []);

    const updateZoom = useCallback(
        (nextZoom: number, clientX?: number) => {
            const viewport = viewportRef.current;
            const normalizedZoom = clamp(nextZoom, MIN_TIMELINE_ZOOM, MAX_TIMELINE_ZOOM);

            if (!viewport || normalizedZoom === zoomRef.current) return;

            const bounds = viewport.getBoundingClientRect();
            const pointerX = clamp((clientX ?? bounds.left + bounds.width / 2) - bounds.left, 0, bounds.width);
            const previousWidth = Math.max(viewportWidth, viewportWidth * zoomRef.current);
            const nextWidth = Math.max(viewportWidth, viewportWidth * normalizedZoom);
            const anchorRatio = (viewport.scrollLeft + pointerX) / previousWidth;

            zoomRef.current = normalizedZoom;
            setZoom(normalizedZoom);

            requestAnimationFrame(() => {
                viewport.scrollLeft = anchorRatio * nextWidth - pointerX;
                updateScrollState();
            });
        },
        [updateScrollState, viewportWidth],
    );

    useEffect(() => {
        const viewport = viewportRef.current;

        if (!viewport) return;

        const handleWheel = (event: WheelEvent) => {
            if (event.ctrlKey) {
                event.preventDefault();
                updateZoom(zoomRef.current * Math.exp(-event.deltaY * 0.006), event.clientX);
                return;
            }

            if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                event.preventDefault();
                viewport.scrollLeft += event.deltaY;
                updateScrollState();
            }
        };

        viewport.addEventListener('wheel', handleWheel, { passive: false });
        updateScrollState();

        return () => viewport.removeEventListener('wheel', handleWheel);
    }, [updateScrollState, updateZoom]);

    useEffect(() => {
        zoomRef.current = MIN_TIMELINE_ZOOM;
        setZoom(MIN_TIMELINE_ZOOM);
        if (viewportRef.current) viewportRef.current.scrollLeft = 0;
        requestAnimationFrame(updateScrollState);
    }, [cadence, updateScrollState]);

    const xScale = useMemo(
        () =>
            scaleTime<number>({
                domain: [window.start, displayEnd],
                range: [
                    ACTIVITY_CLUSTER_EDGE_GUTTER,
                    Math.max(ACTIVITY_CLUSTER_EDGE_GUTTER, trackWidth - ACTIVITY_CLUSTER_EDGE_GUTTER),
                ],
            }),
        [displayEnd, trackWidth, window.start],
    );
    const positionedClusters = useMemo(() => mergeNearbyClusters(clusters, (date) => xScale(date)), [clusters, xScale]);
    const tickHierarchy = useMemo(() => getActivityTimelineTickHierarchy(cadence), [cadence]);
    const majorTicks = useMemo(
        () => generateTimelineTicks(window.start, displayEnd, tickHierarchy.major),
        [displayEnd, tickHierarchy.major, window.start],
    );
    const majorTickTimestamps = useMemo(() => new Set(majorTicks.map((tick) => tick.getTime())), [majorTicks]);
    const minorTicks = useMemo(
        () =>
            generateTimelineTicks(window.start, displayEnd, tickHierarchy.minor).filter(
                (tick) => !majorTickTimestamps.has(tick.getTime()),
            ),
        [displayEnd, majorTickTimestamps, tickHierarchy.minor, window.start],
    );
    const structuralTickTimestamps = useMemo(
        () => new Set([...majorTicks, ...minorTicks].map((tick) => tick.getTime())),
        [majorTicks, minorTicks],
    );
    const fineTicks = useMemo(
        () =>
            generateTimelineTicks(window.start, displayEnd, tickHierarchy.fine).filter(
                (tick) => !structuralTickTimestamps.has(tick.getTime()),
            ),
        [displayEnd, structuralTickTimestamps, tickHierarchy.fine, window.start],
    );
    const fineTickOpacity = clamp(
        (zoom - tickHierarchy.fineRevealZoom) / (MAX_TIMELINE_ZOOM - tickHierarchy.fineRevealZoom),
        0,
        0.72,
    );
    const currentDate = new Date();
    const currentX = xScale(currentDate);
    const currentVisible = currentDate >= window.start && currentDate <= displayEnd;
    const currentLabel = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' })
        .format(currentDate)
        .toUpperCase();
    const currentLabelWidth = currentLabel.length * 7 + 16;
    const railY = 76;

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={viewportRef}
                aria-label='Scrollable workspace activity timeline'
                onScroll={updateScrollState}
                style={{
                    height: '15rem',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    overscrollBehavior: 'contain',
                    scrollbarColor: `${token.colorFillSecondary} transparent`,
                    scrollbarGutter: 'stable',
                }}
            >
                <motion.div
                    animate={{ opacity: 1 }}
                    initial={reduceMotion ? false : { opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.2 }}
                    style={{ height: '14rem', position: 'relative', width: trackWidth }}
                >
                    <svg aria-hidden='true' height='100%' style={{ overflow: 'visible' }} width={trackWidth}>
                        <defs>
                            <linearGradient id={gradientId} x1='0%' x2='100%'>
                                <stop offset='0%' stopColor={token.colorBorderSecondary} stopOpacity='0' />
                                <stop offset='4%' stopColor={token.colorBorderSecondary} stopOpacity='1' />
                                <stop offset='96%' stopColor={token.colorBorderSecondary} stopOpacity='1' />
                                <stop offset='100%' stopColor={token.colorBorderSecondary} stopOpacity='0' />
                            </linearGradient>
                        </defs>
                        <Group>
                            <line
                                stroke={`url(#${gradientId})`}
                                strokeWidth={2}
                                x1={0}
                                x2={trackWidth}
                                y1={railY}
                                y2={railY}
                            />
                            {fineTicks.map((tick) => (
                                <circle
                                    key={`fine-${tick.toISOString()}`}
                                    cx={xScale(tick)}
                                    cy={railY}
                                    fill={token.colorTextQuaternary}
                                    opacity={fineTickOpacity}
                                    r={0.8}
                                    style={{ transition: `opacity ${token.motionDurationMid}` }}
                                />
                            ))}
                            {minorTicks.map((tick) => (
                                <circle
                                    key={`minor-${tick.toISOString()}`}
                                    cx={xScale(tick)}
                                    cy={railY}
                                    fill={token.colorTextQuaternary}
                                    r={1.75}
                                />
                            ))}
                            {majorTicks.map((tick) => {
                                const x = xScale(tick);

                                return (
                                    <g key={`major-${tick.toISOString()}`}>
                                        <line
                                            stroke={token.colorTextQuaternary}
                                            strokeLinecap='round'
                                            strokeWidth={2}
                                            x1={x}
                                            x2={x}
                                            y1={railY - 11}
                                            y2={railY + 11}
                                        />
                                        <text
                                            fill={token.colorTextTertiary}
                                            fontSize={11}
                                            textAnchor='middle'
                                            x={x}
                                            y={42}
                                        >
                                            {formatMajorTimelineTick(tick, cadence)}
                                        </text>
                                    </g>
                                );
                            })}
                            {positionedClusters.map((cluster) => (
                                <line
                                    key={cluster.id}
                                    stroke={token.colorPrimary}
                                    strokeLinecap='round'
                                    strokeWidth={3}
                                    x1={cluster.x}
                                    x2={cluster.x}
                                    y1={railY - 14}
                                    y2={railY + 14}
                                />
                            ))}
                            {currentVisible ? (
                                <g>
                                    <line
                                        stroke={token.colorPrimary}
                                        strokeOpacity={0.72}
                                        strokeWidth={1.5}
                                        x1={currentX}
                                        x2={currentX}
                                        y1={8}
                                        y2={218}
                                    />
                                    <rect
                                        fill={token.colorPrimary}
                                        height={22}
                                        rx={11}
                                        width={currentLabelWidth}
                                        x={currentX - currentLabelWidth / 2}
                                        y={29}
                                    />
                                    <text
                                        fill={token.colorTextLightSolid}
                                        fontSize={11}
                                        textAnchor='middle'
                                        x={currentX}
                                        y={44}
                                    >
                                        {currentLabel}
                                    </text>
                                </g>
                            ) : null}
                        </Group>
                    </svg>
                    {positionedClusters.map((cluster) => (
                        <ActivityClusterDropdown cluster={cluster} key={cluster.id}>
                            <button
                                aria-label={`View ${cluster.events.length} activity events from ${formatTimelineDate(cluster.timestamp)}`}
                                style={{
                                    background: 'transparent',
                                    border: 0,
                                    color: token.colorText,
                                    cursor: 'pointer',
                                    left: cluster.x,
                                    padding: 0,
                                    position: 'absolute',
                                    textAlign: 'center',
                                    top: '6.25rem',
                                    transform: 'translateX(-50%)',
                                    width: ACTIVITY_CLUSTER_WIDTH,
                                }}
                                type='button'
                            >
                                <ActivityClusterAvatarGroups events={cluster.events} />
                                <small
                                    style={{
                                        color: token.colorTextTertiary,
                                        display: 'block',
                                        marginTop: '0.25rem',
                                    }}
                                >
                                    {cluster.events.length} {cluster.events.length === 1 ? 'event' : 'events'}
                                </small>
                                <strong
                                    style={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        paddingInline: '0.25rem',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {cluster.events[0].resourceName}
                                </strong>
                            </button>
                        </ActivityClusterDropdown>
                    ))}
                </motion.div>
            </div>
            <div
                aria-hidden
                style={{
                    background: `linear-gradient(90deg, ${token.colorBgContainer}, transparent)`,
                    bottom: '0.75rem',
                    left: 0,
                    opacity: scrollState.left ? 1 : 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                    top: 0,
                    transition: `opacity ${token.motionDurationMid}`,
                    width: '2rem',
                }}
            />
            <div
                aria-hidden
                style={{
                    background: `linear-gradient(270deg, ${token.colorBgContainer}, transparent)`,
                    bottom: '0.75rem',
                    opacity: scrollState.right ? 1 : 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    transition: `opacity ${token.motionDurationMid}`,
                    width: '2rem',
                }}
            />
        </div>
    );
};

const WorkspaceActivityTimeline: FC<WorkspaceActivityTimelineProps> = ({
    availableCadences,
    cadence,
    error = false,
    events,
    loading = false,
    onCadenceChange,
    onRetry,
    startDate,
}) => {
    const { token } = useAntDesignTheme();
    const gradientId = useId().replace(/:/g, '');
    const [cadenceOpen, setCadenceOpen] = useState(false);
    const cadenceDefinition = getActivityTimelineCadence(cadence);
    const [anchor] = useState(() => new Date());
    const window = useMemo(() => getActivityTimelineWindow(cadence, anchor, startDate), [anchor, cadence, startDate]);
    const clusters = useMemo(
        () => aggregateActivityTimelineEvents(events, cadence, anchor, startDate),
        [anchor, cadence, events, startDate],
    );

    const stepCadence = (direction: -1 | 1) => {
        const nextCadence = stepActivityTimelineCadence(cadence, direction, availableCadences);
        if (nextCadence !== cadence) {
            onCadenceChange(nextCadence);
        }
    };
    const renderCadenceOption = (option: (typeof ACTIVITY_TIMELINE_CADENCES)[number]) => {
        const available = availableCadences.includes(option.id);

        return (
            <Menu.Item
                disabled={!available}
                key={option.id}
                onClick={() => {
                    if (available) {
                        onCadenceChange(option.id);
                        setCadenceOpen(false);
                    }
                }}
            >
                <DropdownOptionContent
                    extra={!available ? <TbLock size='0.875rem' /> : undefined}
                    metadata={[option.minimumPlan === 'enterprise' ? 'Enterprise plan' : 'Plus and Enterprise plans']}
                    selected={option.id === cadence}
                    title={option.label}
                />
            </Menu.Item>
        );
    };

    const cadencePanel = (
        <DropdownPanel width='17rem'>
            <DropdownMenuLabel icon={<TbTimeline size='0.875rem' />}>Timeline Cadence</DropdownMenuLabel>
            <DropdownPanelMenu selectedKeys={[cadence]} selectable>
                {ACTIVITY_TIMELINE_CADENCES.filter(({ id }) => id !== 'all-time').map(renderCadenceOption)}
                <Menu.Divider />
                {ACTIVITY_TIMELINE_CADENCES.filter(({ id }) => id === 'all-time').map(renderCadenceOption)}
            </DropdownPanelMenu>
        </DropdownPanel>
    );

    const controls = (
        <Space.Compact size='small'>
            <Button
                aria-label='Zoom in activity timeline'
                disabled={cadence === availableCadences[0]}
                icon={<TbPlus size='1rem' style={{ color: token.colorTextTertiary }} />}
                onClick={() => stepCadence(-1)}
                size='small'
            />
            <Dropdown
                destroyOnHidden
                open={cadenceOpen}
                popupRender={() => cadencePanel}
                placement='bottomRight'
                trigger={['click']}
                onOpenChange={setCadenceOpen}
            >
                <Button size='small'>
                    {cadenceDefinition.label}{' '}
                    <TbChevronDown size='1rem' style={{ color: token.colorTextTertiary, verticalAlign: '-0.15rem' }} />
                </Button>
            </Dropdown>
            <Button
                aria-label='Zoom out activity timeline'
                disabled={cadence === availableCadences[availableCadences.length - 1]}
                icon={<TbMinus size='1rem' style={{ color: token.colorTextTertiary }} />}
                onClick={() => stepCadence(1)}
                size='small'
            />
        </Space.Compact>
    );

    return (
        <AnalyticsSectionCard
            ariaLabel='Workspace activity timeline'
            description='Explore workspace events across the selected cadence. Pinch to zoom.'
            extra={controls}
            icon={<TbTimeline />}
            title='Activity Timeline'
        >
            {loading ? (
                <TimelineSkeleton />
            ) : error ? (
                <EmptyView
                    description='Workspace activity could not be loaded. No usage data or permissions were changed.'
                    extra={
                        onRetry ? (
                            <Button size='small' type='link' onClick={onRetry}>
                                Try Again
                            </Button>
                        ) : null
                    }
                    height='13rem'
                    icon={<TbExclamationCircle size='1.25rem' />}
                    label='Unable to Load Activity'
                />
            ) : clusters.length ? (
                <div style={{ height: '15rem', position: 'relative' }}>
                    <ParentSize>
                        {({ width }) => (
                            <TimelineViewport
                                cadence={cadence}
                                clusters={clusters}
                                gradientId={gradientId}
                                viewportWidth={width}
                                window={window}
                            />
                        )}
                    </ParentSize>
                </div>
            ) : (
                <EmptyView
                    description='Workspace events in this time window will appear on the timeline.'
                    height='13rem'
                    icon={<TbTimeline size='1.25rem' />}
                    label='No Activity in This Window'
                />
            )}
        </AnalyticsSectionCard>
    );
};

export default WorkspaceActivityTimeline;
